import { useTranslation } from "react-i18next";
import PressArticleFeature from "@/pages/nostalgie/components/PressArticleFeature";
import PressScanFeature from "@/pages/nostalgie/components/PressScanFeature";
import ArticlePhotoStrip from "@/pages/nostalgie/components/ArticlePhotoStrip";
import {
  articlePhotosByIndex,
  pressPhotoByIndex,
} from "@/pages/nostalgie/data/nostalgiaPhotos";
import { fazBlocks, fazSource } from "@/pages/nostalgie/data/fazArticle";
import { ftdBlocks, ftdSource, ftdStandfirst } from "@/pages/nostalgie/data/ftdArticle";
import {
  sonntagszeitungBlocks,
  sonntagszeitungSource,
  sonntagszeitungStandfirst,
} from "@/pages/nostalgie/data/sonntagszeitungArticle";
import {
  noenScans,
  salzburgerScans,
  touristikAktuellScans,
} from "@/pages/nostalgie/data/pressScans";

type PressEntry = { index: number; year: number };

const entries: PressEntry[] = [
  { index: 11, year: 2004 },
  { index: 12, year: 2004 },
  { index: 6, year: 2005 },
  { index: 7, year: 2005 },
  { index: 8, year: 2005 },
  { index: 9, year: 2005 },
  { index: 14, year: 2005 },
  { index: 15, year: 2005 },
  { index: 2, year: 2006 },
  { index: 3, year: 2006 },
  { index: 4, year: 2006 },
  { index: 5, year: 2006 },
  { index: 13, year: 2006 },
  { index: 1, year: 2008 },
];

const years = Array.from(new Set(entries.map((entry) => entry.year))).sort(
  (a, b) => a - b,
);

function PressCard({ index }: { index: number }) {
  const { t } = useTranslation();
  const photo = pressPhotoByIndex[index];

  return (
    <article className="group bg-background-100 border border-background-200 rounded-lg overflow-hidden flex flex-col transition-colors hover:border-accent-300">
      {photo && (
        <div className="aspect-[16/10] overflow-hidden bg-background-200">
          <img
            src={photo}
            alt={t(`nost_press_${index}_title`)}
            title={t(`nost_press_${index}_source`)}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      )}

      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
            <i className="ri-newspaper-line text-lg"></i>
          </span>
          <div className="min-w-0">
            <div className="text-xs font-semibold tracking-[0.2em] text-accent-700">
              {t(`nost_press_${index}_year`)}
            </div>
            <div className="text-sm text-foreground-600 leading-snug">
              {t(`nost_press_${index}_source`)}
            </div>
          </div>
        </div>

        <h3 className="font-heading text-lg md:text-xl text-foreground-950 leading-snug mb-3">
          {t(`nost_press_${index}_title`)}
        </h3>
        <p className="text-foreground-700 leading-relaxed text-sm">
          {t(`nost_press_${index}_text`)}
        </p>

        {articlePhotosByIndex[index] && (
          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-[0.2em] text-foreground-500 mb-2">
              {t("nost_press_photos_label")}
            </div>
            <ArticlePhotoStrip photos={articlePhotosByIndex[index]} />
          </div>
        )}
      </div>
    </article>
  );
}

export default function PressArchive() {
  const { t } = useTranslation();

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-3xl mb-10">
          <div className="eyebrow-brand text-xs uppercase tracking-[0.35em] mb-3">
            — {t("nost_press_eyebrow")} —
          </div>
          <h2 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-[1.1] tracking-tight mb-5">
            {t("nost_press_title")}
          </h2>
          <p className="text-foreground-700 leading-relaxed text-base md:text-lg">
            {t("nost_press_intro")}
          </p>
        </div>

        <div className="space-y-12 md:space-y-16">
          {years.map((year) => {
            const yearEntries = entries.filter((entry) => entry.year === year);
            return (
              <div key={year}>
                <div className="flex items-center gap-4 md:gap-6 mb-6 md:mb-8">
                  <div className="font-heading text-4xl md:text-5xl text-accent-700 leading-none tracking-tight">
                    {year}
                  </div>
                  <div className="h-px flex-1 bg-background-300" />
                  <div className="text-xs uppercase tracking-[0.25em] text-foreground-600 whitespace-nowrap">
                    {yearEntries.length} {t("nost_press_count_label")}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
                  {yearEntries.map((entry) => {
                    if (entry.index === 2) {
                      return (
                        <PressArticleFeature
                          key="faz-feature"
                          index={2}
                          blocks={fazBlocks}
                          source={fazSource}
                          bylineKey="nost_press_2_byline"
                        />
                      );
                    }
                    if (entry.index === 9) {
                      return (
                        <PressArticleFeature
                          key="ftd-feature"
                          index={9}
                          blocks={ftdBlocks}
                          source={ftdSource}
                          standfirst={ftdStandfirst}
                          bylineKey="nost_press_9_byline"
                        />
                      );
                    }
                    if (entry.index === 13) {
                      return (
                        <PressScanFeature
                          key="press-scan-touristik"
                          index={13}
                          scans={touristikAktuellScans}
                        />
                      );
                    }
                    if (entry.index === 15) {
                      return (
                        <PressArticleFeature
                          key="sonntagszeitung-feature"
                          index={15}
                          blocks={sonntagszeitungBlocks}
                          source={sonntagszeitungSource}
                          standfirst={sonntagszeitungStandfirst}
                          bylineKey="nost_press_15_byline"
                          photos={articlePhotosByIndex[15]}
                        />
                      );
                    }
                    if (entry.index === 14) {
                      return (
                        <PressScanFeature
                          key="press-scan-noen"
                          index={14}
                          scans={noenScans}
                        />
                      );
                    }
                    if (entry.index === 11) {
                      return (
                        <PressScanFeature
                          key="press-scan-salzburger"
                          index={11}
                          scans={salzburgerScans}
                        />
                      );
                    }
                    return <PressCard key={entry.index} index={entry.index} />;
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}